/* eslint-disable max-lines */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Row, Col } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { useFormContext, useWatch } from "react-hook-form";
import moment from "moment";
import momentTZ from "moment-timezone";
import { getFile } from "api";
import UnitConverter from "components/organisms/UnitConverter/UnitConverter";
import { RequestFormState } from "pages/shipper/RequestForm/RequestFormState";
import { getObjectByKeys } from "helpers/commonHelpers";
import { calculateGoodItemsTotalWeight, getBaseWeightUnit } from "helpers/quotationFormHelper";
import { getAllFiles } from "helpers/requestFormHelper";
import { StopAddressType, StopTimeType } from "types/MultiStop";
import { IDangerousGoods, IFiles, IAttachmentFile } from "../PackingList/Types";
import { temperatureControlledOptions as TempControlOptions } from "../PackingList/PackingListDefaultOptions";

interface IPackingListProps {
  state: RequestFormState;
}

interface GoodCustomsInformation {
  value: number;
}

type NmfcOption = [string, string];
interface GoodItem {
  cbm_scope: string;
  units: number;
  m_unit: string;
  w_unit?: string | null;
  weight_piece: number;
  length: number;
  width: number;
  height: number;
  good_customs_information?: GoodCustomsInformation[];
  handling_unit: string;
  l_w_h_cbm_piece?: number;
  l_w_h_cbm_total?: number;
  pickup_stop_key?: string | null;
  delivery_stop_key?: string | null;
}

function PackingList({ state }: IPackingListProps) {
  const intl = useIntl();
  const methods = useFormContext();
  const { getValues, watch, control } = methods;
  const { packingList } = state || {};
  const [allFiles, setAllFiles] = useState<IFiles[]>([]);
  const temperatureControlledOptions = useMemo<any>(() => getObjectByKeys(TempControlOptions(intl), "value"), []);

  const watchPackingList = useWatch({
    control,
    name: "packing_list",
  });

  const globalWUnit = useWatch({
    control,
    name: "packing.global_configuration.w_unit",
  });

  const watchStops = useWatch({
    control,
    name: "schedule.stops",
  });

  const [totalPiece, setTotalPiece] = useState(0);
  const [totalWeight, setTotalWeight] = useState(0);
  const [totalWeightUnit, setTotalWeightUnit] = useState("kg");
  const [totalCBM, setTotalCBM] = useState(0);
  const [totalCustomValue, setTotalCustomValue] = useState(0);

  const stopByKey = useMemo(() => {
    const stops = Array.isArray(watchStops) ? watchStops : [];
    return new Map(stops.filter((s: any) => s?.stop_key).map((s: any) => [s.stop_key, s]));
  }, [watchStops]);

  // WEB-11639: only show packing-list stop details for multi-drop / multi-stop.
  const showPackingListStopDetails = Array.isArray(watchStops) && watchStops.length > 2;

  const formatStopDateAndTime = useCallback(
    ({ date, time, timezone }: { date?: string; time?: string; timezone?: string }) => {
      if (!date || !time || !timezone) return "-";
      const dateTime = `${date}T${time}`;
      const formattedDate = momentTZ.tz(dateTime, timezone).format("DD MMM YYYY");
      const formattedTime = momentTZ.tz(dateTime, timezone).format("HH:mm ([UTC] Z)");
      return `${formattedDate}, ${formattedTime}`;
    },
    [],
  );

  const getStopAddressTypeFlags = useCallback((stop_address_type?: any) => {
    const isAirport = ["Airport", StopAddressType.AIRPORT].includes(stop_address_type ?? "");
    const isSeaport = ["Port", StopAddressType.SEAPORT].includes(stop_address_type ?? "");
    return { isAirport, isSeaport };
  }, []);

  const renderStopDetails = useCallback(
    (options: { prefixText: string; stopKey?: string | null }) => {
      const { prefixText, stopKey } = options;
      if (!stopKey) return null;

      const stop: any = stopByKey.get(stopKey);
      if (!stop) {
        return (
          <Row className="mb-2">
            <Col className="text-secondary">{prefixText}</Col>
            <Col>
              <FormattedMessage id="general.missing" defaultMessage="Missing" />
            </Col>
          </Row>
        );
      }

      const stop_time_type = stop?.stop_time_type;
      const stop_timezone = stop?.stop_timezone;

      const isAfter = ["After", StopTimeType.AFTER].includes(stop_time_type ?? "");
      const isBefore = ["Before", StopTimeType.BEFORE].includes(stop_time_type ?? "");
      const isBetween = ["Between", StopTimeType.BETWEEN].includes(stop_time_type ?? "");
      const isASAPDelivery = ["ASAP", StopTimeType.ASAP_DELIVERY].includes(stop_time_type ?? "");

      const afterDateTime = formatStopDateAndTime({
        date: stop?.stop_after_date,
        time: stop?.stop_after_time,
        timezone: stop_timezone,
      });
      const beforeDateTime = formatStopDateAndTime({
        date: stop?.stop_before_date,
        time: stop?.stop_before_time,
        timezone: stop_timezone,
      });

      const afterText = intl.formatMessage({ id: "general.after", defaultMessage: "after" });
      const beforeText = intl.formatMessage({ id: "general.before", defaultMessage: "before" });

      const { isAirport, isSeaport } = getStopAddressTypeFlags(stop?.stop_address_type);

      const streetAddress = stop?.street_address || "";
      const city = stop?.city_name || "";
      const zipcode = stop?.zipcode || "";
      const state = stop?.state_name || "";
      const country = stop?.country_name || "";

      const cityAndPostalCode = city || zipcode ? `${city ?? ""}${city && zipcode ? ", " : " "}${zipcode ?? ""}` : "";
      const stateAndCountry = state || country ? `${state ?? ""}${state && country ? ", " : " "}${country ?? ""}` : "";

      const withPrefix = (label: React.ReactNode) => (
        <>
          {prefixText} {label}
        </>
      );

      const rows: { label: React.ReactNode; value: React.ReactNode }[] = [
        {
          label: withPrefix(<FormattedMessage id="quote-request-detail.company" defaultMessage="Company" />),
          value: stop?.address_name || "-",
        },
        {
          label: withPrefix(<FormattedMessage id="general.time" defaultMessage="Time" />),
          value: (
            <div className="text-capitalize">
              {isAfter && (
                <>
                  {afterText} {afterDateTime}
                </>
              )}
              {isBefore && (
                <>
                  {beforeText} {beforeDateTime}
                </>
              )}
              {isBetween && (
                <>
                  {afterDateTime} - {beforeDateTime}
                </>
              )}
              {isASAPDelivery && (
                <FormattedMessage
                  id="new-request.schedule.defaultCargoReadyDeliveryOption.asap"
                  defaultMessage="ASAP Delivery"
                />
              )}
              {!isAfter && !isBefore && !isBetween && !isASAPDelivery && "-"}
            </div>
          ),
        },
        ...(isAirport
          ? [
              {
                label: withPrefix(<FormattedMessage id="general.airport" defaultMessage="Airport" />),
                value:
                  stop?.stop_airport_name && stop?.stop_airport_code
                    ? `${stop?.stop_airport_name} (${stop?.stop_airport_code})`
                    : "-",
              },
            ]
          : []),
        ...(isSeaport
          ? [
              {
                label: withPrefix(<FormattedMessage id="general.seaport" defaultMessage="Seaport" />),
                value:
                  stop?.stop_seaport_name && stop?.stop_seaport_code
                    ? `${stop?.stop_seaport_name} (${stop?.stop_seaport_code})`
                    : "-",
              },
            ]
          : []),
        {
          label: withPrefix(<FormattedMessage id="quote-request-detail.address" defaultMessage="Address" />),
          value: streetAddress || "-",
        },
        {
          label: withPrefix(
            <FormattedMessage id="quote-request-detail.cityAndPostalCode" defaultMessage="City and postal code" />,
          ),
          value: cityAndPostalCode || "-",
        },
        {
          label: withPrefix(
            <FormattedMessage id="quote-request-detail.stateAndCountry" defaultMessage="State and country" />,
          ),
          value: stateAndCountry || "-",
        },
      ];

      return (
        <>
          {rows.map((row, idx) => (
            <Row key={`${stopKey}-${idx}`} className="mb-2">
              <Col className="text-secondary">{row.label}</Col>
              <Col>{row.value}</Col>
            </Row>
          ))}
        </>
      );
    },
    [formatStopDateAndTime, getStopAddressTypeFlags, intl, stopByKey],
  );

  // Returns the NMFC description for the given item index
  const getNmfcClassDescription = useCallback(
    (index) => {
      const selectedNmfc = packingList?.nmfcOptions?.find(
        (nmfc: NmfcOption) => nmfc[0] === getValues(`packing_list.${index}.nmfc_class`),
      );

      return selectedNmfc ? selectedNmfc[1] : ""; // Return the description or an empty string if not found
    },
    [packingList.nmfcOptions],
  );

  const getDangerousGood = useCallback(
    (index) =>
      packingList?.dangerousGoods?.length > 0 &&
      packingList?.dangerousGoods?.filter(
        (nmfc: IDangerousGoods) => nmfc.value === getValues(`packing_list.${index}.dangerous_good_value`),
      ),
    [packingList.dangerousGoods],
  );

  const getAttachments = () => {
    if (watch("packing_list")?.length > 0) {
      for (let index = 0; index < watch("packing_list")?.length; index += 1) {
        const goodCustomInfo = watch(`packing_list.${index}.good_customs_information`);
        if (goodCustomInfo?.length > 0) {
          goodCustomInfo.forEach((file: IAttachmentFile) => {
            const referenceNumber = file.attachment && file?.attachment[0];
            if (file?.attachment?.length > 0) {
              getFile(referenceNumber).then((res) => {
                const fileNameWithoutPath = res.data.name.substring(res.data.name.lastIndexOf("/") + 1);
                const file = {
                  fileName: fileNameWithoutPath,
                  url: res.data.url,
                  path: res.data.name,
                  reference: res.data.reference_number,
                  commodity: index,
                };
                setAllFiles((allFiles) => [...allFiles, file]);
                if (allFiles?.length > 0) {
                  setAllFiles((allFiles) => getAllFiles(allFiles));
                }
              });
              const files = allFiles.filter((file: any) => file.reference === referenceNumber);
              setAllFiles(files);
            }
          });
        }
      }
    }
  };

  useEffect(() => {
    getAttachments();
    const packingListData = Array.isArray(watchPackingList) ? watchPackingList : [];
    const totalPieces = packingListData.reduce((acc: number, item: GoodItem) => acc + Number(item.units), 0);
    const targetUnit = getBaseWeightUnit(globalWUnit ?? packingListData[0]?.w_unit);
    const { weight } = calculateGoodItemsTotalWeight(packingListData, targetUnit);

    const totalCBM = packingListData.reduce((acc: number, item: GoodItem) => {
      let cbm = 1;
      if (
        packingList.handlingUnitOptions?.[item?.handling_unit] &&
        packingList.handlingUnitOptions?.[item?.handling_unit]?.l_w_h_cbm
      ) {
        cbm =
          (item.l_w_h_cbm_piece
            ? Number(item.l_w_h_cbm_piece) * (item.cbm_scope === "piece" ? Number(item.units) : 1)
            : Number(item.l_w_h_cbm_total)) || 1;
      } else {
        let length = Number(item.length);
        let width = Number(item.width);
        let height = Number(item.height);
        const units = Number(item.units);

        if (item.m_unit === "inch") {
          // convert inches to centimeters
          const cmPerInch = 2.54;
          length *= cmPerInch;
          width *= cmPerInch;
          height *= cmPerInch;
        }
        cbm = (length * width * height * units) / 1000000; // converting to cbm
        if (item.cbm_scope === "piece") cbm *= Number(item.units);
      }
      cbm = Number(cbm) || 0;
      return acc + cbm;
    }, 0);

    const totalCustomsValue = packingListData.reduce((acc: number, item: GoodItem) => {
      const customsInfo = item.good_customs_information;
      if (customsInfo && customsInfo.length > 0) {
        return acc + Number(customsInfo[0].value);
      }
      return acc;
    }, 0);

    setTotalPiece(totalPieces);
    setTotalWeight(weight);
    setTotalWeightUnit(targetUnit);
    setTotalCBM(totalCBM);
    setTotalCustomValue(totalCustomsValue);
  }, [watchPackingList, globalWUnit]);

  const getWeightUnit = (wUnit?: string | null): string => {
    if (wUnit === "total_kg") return "kg";
    if (wUnit === "total_lbs") return "lbs";
    return wUnit ?? totalWeightUnit ?? "";
  };

  return (
    <div className="font-b3">
      <Row className="mb-2">
        <Col className="text-secondary">
          <FormattedMessage id="quote-request-detail.totalPackages" defaultMessage="Total packages" />
        </Col>
        <Col>{`${totalPiece} Units`}</Col>
      </Row>
      {!isNaN(totalWeight) && !!totalWeight && (
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage id="quote-request-detail.totalWeight" defaultMessage="Total weight" />
          </Col>
          <Col>
            <UnitConverter
              content={<span>{`${totalWeight} ${totalWeightUnit}`}</span>}
              value={totalWeight}
              unit={totalWeightUnit}
            />
          </Col>
        </Row>
      )}
      <Row className="mb-2">
        <Col className="text-secondary">
          <FormattedMessage id="new-request.quote-confirmation.packing-list.CBM" defaultMessage="Total CBM" />
        </Col>
        <Col>{isNaN(totalCBM) ? 0 : totalCBM.toFixed(2)} m³</Col>
      </Row>
      <Row className="mb-2">
        <Col className="text-secondary">
          <FormattedMessage id="quote-request-detail.totalCustomsValue" defaultMessage="Total customs value" />
        </Col>
        <Col>{`${totalCustomValue} ${
          getValues(`packing_list.${0}.good_customs_information.${0}.currency_code`) || ""
        }`}</Col>
      </Row>
      {getValues("organizational.goods_confirmation_type") && (
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage id="new-request.organisational-GoodListEstimation" defaultMessage="Goods list estimation" />
          </Col>
          <Col>{getValues("organizational.goods_confirmation_type")}</Col>
        </Row>
      )}
      <hr />
      {Array.isArray(watchPackingList) &&
        watchPackingList.length > 0 &&
        watchPackingList.map((item: any, index: number) => (
          <div key={Math.random()} className="commodity-line">
            <Row className="mb-2 mt-3">
              <Col className="text-secondary">
                <FormattedMessage id="quote-request-detail.commodity" defaultMessage="Commodity" />
                <span>{` #${index + 1}`}</span>
              </Col>
              <Col className=" text-capitalize" />
            </Row>
            {item.handling_unit && (
              <Row className="mb-2 mt-3">
                <Col className="text-secondary">
                  <FormattedMessage id="new-request.packing-list.handlingUnit" defaultMessage="Handling unit" />
                </Col>
                <Col className=" text-capitalize">{item.handling_unit}</Col>
              </Row>
            )}
            {item.length && item.width && item.height && (
              <Row className="mb-2">
                <Col className="text-secondary">LxWxH</Col>
                <Col className=" d-flex">
                  <UnitConverter content={item.length} value={item.length} unit={item.m_unit} />
                  <span className="mx-1">x</span>
                  <UnitConverter content={item.width} value={item.width} unit={item.m_unit} />
                  <span className="mx-1">x</span>
                  <UnitConverter content={item.height} value={item.height} unit={item.m_unit} />
                  {item.m_unit}
                </Col>
              </Row>
            )}
            {item.additional_field && (
              <Row className="mb-2">
                <Col className="text-secondary">
                  {packingList?.handlingUnitOptions?.[item?.handling_unit]?.additional_field_label ||
                    intl.formatMessage({
                      id: "new-request.packing-list.additionalField",
                      defaultMessage: "Additional field",
                    })}
                </Col>
                <Col>{item.additional_field}</Col>
              </Row>
            )}
            {item.weight_piece && (
              <Row className="mb-2">
                <Col className="text-secondary">
                  <FormattedMessage id="new-request.packing-list.unitWeight" defaultMessage="Unit weight" />
                </Col>
                <Col>
                  <UnitConverter
                    content={
                      <span>
                        {item.weight_piece}{" "}
                        {getWeightUnit(item.w_unit ?? getValues("packing.global_configuration.w_unit"))}
                      </span>
                    }
                    value={item.weight_piece}
                    unit={getWeightUnit(item.w_unit ?? getValues("packing.global_configuration.w_unit"))}
                  />
                </Col>
              </Row>
            )}
            {item.units && (
              <Row className="mb-2">
                <Col className="text-secondary">
                  <FormattedMessage id="new-request.packing-list.numberOfUnits" defaultMessage="Quantity" />
                </Col>
                <Col>{item.units}</Col>
              </Row>
            )}
            {item.stackable && (
              <Row className="mb-2">
                <Col className="text-secondary">
                  <FormattedMessage id="quote-request-detail.stackable" defaultMessage="Stackable" />
                </Col>
                <Col>
                  {item.stackable
                    ? intl.formatMessage({ id: "general.yes", defaultMessage: "Yes" })
                    : intl.formatMessage({ id: "general.no", defaultMessage: "No" })}
                </Col>
              </Row>
            )}

            <Row className="mb-2">
              <Col className="text-secondary">
                <FormattedMessage
                  id="new-request.packing-list.temperatureControlled"
                  defaultMessage="Temperature controlled"
                />
              </Col>
              <Col>
                {item.temperature_control_required
                  ? `${intl.formatMessage({ id: "general.yes", defaultMessage: "Yes" })}${
                      item.temperature_control_value
                        ? `, ${temperatureControlledOptions[item?.temperature_control_value]?.label}`
                        : ""
                    }${item.temperature_control_value === "6" ? ` (${item.temperature_controlled_custom_value})` : ""}
                    `
                  : intl.formatMessage({ id: "general.no", defaultMessage: "No" })}
              </Col>
            </Row>

            {item.crating_required && (
              <Row className="mb-2">
                <Col className="text-secondary">
                  <FormattedMessage id="new-request.packing-list.cratingRequired" defaultMessage="Crating required" />
                </Col>
                <Col>
                  <FormattedMessage
                    id="new-request.quote-confirmation.packing-list.cratingValue"
                    defaultMessage="Yes (By {crating_required_by})"
                    values={{
                      crating_required_by: `${moment(item.crating_required_by).format("YYYY-MM-DD")}`,
                    }}
                  />
                </Col>
              </Row>
            )}
            {item.hazardous && (
              <Row className="mb-2">
                <Col className="text-secondary">
                  <FormattedMessage id="new-request.packing-list.hazardous" defaultMessage="Hazardous" />
                </Col>
                <Col>
                  {item.hazardous
                    ? intl.formatMessage({ id: "general.yes", defaultMessage: "Yes" })
                    : intl.formatMessage({ id: "general.no", defaultMessage: "No" })}
                </Col>
              </Row>
            )}
            {item.is_dangerous_good && (
              <Row className="mb-2">
                <Col className="text-secondary">
                  <FormattedMessage id="new-request.packing-list.dangerousGoods" defaultMessage="Dangerous goods" />
                </Col>
                <Col>
                  {item.is_dangerous_good
                    ? `Yes - ${getDangerousGood(index)[0]?.label}`
                    : intl.formatMessage({ id: "general.no", defaultMessage: "No" })}
                </Col>
              </Row>
            )}
            {item.is_dangerous_good && item.un_number && (
              <Row className="mb-2">
                <Col className="text-secondary">
                  <FormattedMessage id="new-request.packing-list.unNumber" defaultMessage="UN Number" />
                </Col>
                <Col>{item.un_number}</Col>
              </Row>
            )}
            {item.temperature_controlled_select && (
              <Row className="mb-2">
                <Col className="text-secondary">
                  <FormattedMessage
                    id="new-request.packing-list.temperatureControlled"
                    defaultMessage="Temperature controlled"
                  />
                </Col>
                <Col>
                  {item.temperature_controlled_select
                    ? intl.formatMessage({ id: "general.yes", defaultMessage: "Yes" })
                    : intl.formatMessage({ id: "general.no", defaultMessage: "No" })}
                  <span>,</span>
                  <span className="ml-2">
                    {item.temperature_control_value && (
                      <FormattedMessage
                        id="new-request.packing-list.temperatureControlledSelect"
                        defaultMessage="item.temperature_controlled_select"
                        values={item.temperature_control_value}
                      />
                    )}
                  </span>
                </Col>
              </Row>
            )}
            {item.nmfc_class && (
              <Row className="mb-2">
                <Col className="text-secondary">
                  <FormattedMessage id="new-request.packing-list.NMFCN" defaultMessage="NMFC Number" />
                </Col>
                <Col>{item.nmfc_class}</Col>
              </Row>
            )}
            {item.nmfc_class && (
              <Row className="mb-2">
                <Col className="text-secondary">
                  <FormattedMessage id="new-request.packing-list.NMFC" defaultMessage="NMFC Class" />
                </Col>
                <Col>{getNmfcClassDescription(index)}</Col>
              </Row>
            )}
            {item.additional_information && (
              <Row className="mb-5">
                <Col className="text-secondary">
                  <FormattedMessage
                    id="new-request.quote-confirmation.schedule.additionalInformation"
                    defaultMessage="Additional information"
                  />
                </Col>
                <Col>{item.additional_information}</Col>
              </Row>
            )}
            {item.handling_required && (
              <Row className="mb-5">
                <Col className="text-secondary">
                  <FormattedMessage id="new-request.packing-list.handlingRequired" defaultMessage="Handling required" />
                </Col>
                <Col>
                  {" "}
                  {item.handling_required
                    ? intl.formatMessage({ id: "general.yes", defaultMessage: "Yes" })
                    : intl.formatMessage({ id: "general.no", defaultMessage: "No" })}
                </Col>
              </Row>
            )}
            {watch(`packing_list.${index}.good_customs_information.${0}.attachment`)?.length > 0 && (
              <Row className="mb-2">
                <Col className="text-secondary">
                  <FormattedMessage id="new-request.packing-list.customsFile" defaultMessage="Customs file" />
                </Col>
                <Col className="text-secondary d-flex flex-column mt-1">
                  {getAllFiles(allFiles)?.length > 0 &&
                    getAllFiles(allFiles).map(
                      (file: any) =>
                        file.commodity === index && (
                          <a target="_blank" href={file.url} rel="noopener noreferrer" download>
                            {file.fileName}
                          </a>
                        ),
                    )}
                </Col>
              </Row>
            )}

            {item.good_customs_information?.length > 0 && (
              <Row>
                <Col className="text-secondary">
                  <FormattedMessage
                    id="new-request.quote-confirmation.packing-list.customInformation"
                    defaultMessage="Customs information"
                  />
                </Col>
                <Col>
                  {item?.good_customs_information?.map((customs_info: any) => (
                    <div key={Math.random()}>
                      {customs_info.description && (
                        <>
                          <div className="d-flex flex-row justify-content-between">
                            <span>{customs_info.description}</span>
                            <span>{`${customs_info.value ?? ""} ${customs_info.currency_code}`}</span>
                          </div>
                          {customs_info.hs_code && (
                            <div className="d-flex flex-row justify-content-between">
                              <FormattedMessage
                                id="new-request.quote-confirmation.packing-list.HsCode"
                                defaultMessage="HS Code:"
                              />
                              <span className="ml-auto">{customs_info.hs_code}</span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </Col>
              </Row>
            )}

            {showPackingListStopDetails && (item?.pickup_stop_key || item?.delivery_stop_key) && (
              <div className="my-5" />
            )}
            {showPackingListStopDetails &&
              renderStopDetails({
                prefixText: intl.formatMessage({ id: "general.pickup", defaultMessage: "Pickup" }),
                stopKey: item?.pickup_stop_key,
              })}
            {showPackingListStopDetails && item?.pickup_stop_key && item?.delivery_stop_key && (
              <div className="my-5" />
            )}
            {showPackingListStopDetails &&
              renderStopDetails({
                prefixText: intl.formatMessage({ id: "general.delivery", defaultMessage: "Delivery" }),
                stopKey: item?.delivery_stop_key,
              })}
            <hr />
          </div>
        ))}
    </div>
  );
}

export default PackingList;
